import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0C]/95 backdrop-blur-sm z-10 px-4 py-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">Privacy Policy</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 max-w-3xl mx-auto">
        <p className="text-white/50 mb-6">Effective date: January 17, 2026</p>
        
        <p className="text-white/80 mb-6">
          Cigar Ranker ("Cigar Ranker," "we," "us," or "our") operates the website located at{' '}
          <a href="https://cigar-ranker.com" className="text-amber-500 hover:underline">
            https://cigar-ranker.com
          </a>{' '}
          (the "Service").
        </p>
        
        <p className="text-white/80 mb-8">
          This Privacy Policy explains what information we collect, how we use it, and the choices you have.
        </p>

        {/* Section 1 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">1) Information We Collect</h2>
        
        <h3 className="text-lg font-semibold text-white mb-2">A. Information you provide</h3>
        <p className="text-white/80 mb-4">When you create an account or use the Service, you may provide:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li><strong>Account information:</strong> email address, username, password (stored in encrypted/hashed form)</li>
          <li><strong>User content:</strong> cigar ratings, reviews, comments, lists, favorites, or other content you submit</li>
          <li><strong>Contact information:</strong> if you email us or submit a support request</li>
        </ul>
        
        <h3 className="text-lg font-semibold text-white mb-2">B. Information collected automatically</h3>
        <p className="text-white/80 mb-4">When you visit the Service, we may automatically collect:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li><strong>Device and usage data:</strong> pages viewed, clicks, referring/exit pages, time spent, and other activity</li>
          <li><strong>Technical data:</strong> IP address, browser type, operating system, device identifiers</li>
          <li><strong>Approximate location:</strong> inferred from IP address (city/region level)</li>
        </ul>
        
        <h3 className="text-lg font-semibold text-white mb-2">C. Cookies and similar technologies</h3>
        <p className="text-white/80 mb-2">We may use cookies and similar technologies to:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li>keep you signed in</li>
          <li>remember preferences</li>
          <li>understand how the Service is used</li>
          <li>measure marketing performance (for example, UTM parameters)</li>
        </ul>
        <p className="text-white/70 mb-8">
          You can control cookies through your browser settings. Some features may not work properly if cookies are disabled.
        </p>

        {/* Section 2 - Data Storage */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">2) How We Store Your Data</h2>
        <p className="text-white/80 mb-4">We take data security seriously. Here's how your information is stored and protected:</p>
        
        <h3 className="text-lg font-semibold text-white mb-2">A. Password security</h3>
        <p className="text-white/70 mb-4">
          Your password is never stored in plain text. We use industry-standard hashing algorithms (bcrypt) to securely hash your password before storing it. This means even we cannot see your actual password.
        </p>
        
        <h3 className="text-lg font-semibold text-white mb-2">B. Database storage</h3>
        <p className="text-white/70 mb-4">
          Your account information, ratings, reviews, and other user-generated content are stored in secure MongoDB databases hosted on encrypted infrastructure.
        </p>
        
        <h3 className="text-lg font-semibold text-white mb-2">C. Image storage</h3>
        <p className="text-white/70 mb-4">
          Photos you upload (such as profile pictures or cigar images) are stored on Cloudflare R2, a secure cloud object storage service. Images are served over HTTPS and access is controlled through our application.
        </p>
        
        <h3 className="text-lg font-semibold text-white mb-2">D. Data transmission</h3>
        <p className="text-white/70 mb-4">
          All data transmitted between your device and our servers is encrypted using TLS (Transport Layer Security). This protects your information while in transit.
        </p>
        
        <h3 className="text-lg font-semibold text-white mb-2">E. AI processing</h3>
        <p className="text-white/70 mb-8">
          When you use AI-powered features (such as cigar identification from photos), your images are processed by our AI service provider. These images are used solely for the purpose of providing the service and are not retained for training purposes.
        </p>

        {/* Section 3 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">3) How We Use Information</h2>
        <p className="text-white/80 mb-2">We use the information we collect to:</p>
        <ul className="list-disc list-inside text-white/70 mb-8 space-y-1 ml-4">
          <li>provide, operate, and maintain the Service</li>
          <li>create and manage user accounts</li>
          <li>store and display your ratings and other content</li>
          <li>improve features, performance, and user experience</li>
          <li>monitor for abuse, spam, and security issues</li>
          <li>communicate with you about the Service (including support and important updates)</li>
          <li>measure and optimize marketing and traffic sources</li>
        </ul>

        {/* Section 4 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">4) How We Share Information</h2>
        <p className="text-white/80 mb-4">We do not sell your personal information.</p>
        <p className="text-white/80 mb-4">We may share information in the following situations:</p>
        
        <h3 className="text-lg font-semibold text-white mb-2">A. Service providers</h3>
        <p className="text-white/80 mb-2">We may share information with vendors that help us operate the Service, including:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li>Cloudflare (hosting, performance, and security)</li>
          <li>Resend (email delivery)</li>
          <li>Emergent (service provider)</li>
        </ul>
        <p className="text-white/70 mb-4">These providers are permitted to use your information only to perform services for us.</p>
        
        <h3 className="text-lg font-semibold text-white mb-2">B. Legal and safety reasons</h3>
        <p className="text-white/80 mb-2">We may disclose information if we believe it is necessary to:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li>comply with applicable laws, regulations, or legal requests</li>
          <li>enforce our Terms of Service</li>
          <li>protect the rights, safety, and security of users, the public, or the Service</li>
        </ul>
        
        <h3 className="text-lg font-semibold text-white mb-2">C. Business transfers</h3>
        <p className="text-white/70 mb-8">
          If we are involved in a merger, acquisition, financing, or sale of assets, information may be transferred as part of that transaction.
        </p>

        {/* Section 5 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">5) Public Content and Visibility</h2>
        <p className="text-white/80 mb-2">Some information you submit may be visible to other users or the public, such as:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li>your username/profile</li>
          <li>your cigar ratings, reviews, and lists</li>
        </ul>
        <p className="text-white/70 mb-8">You should not post personal information in content you want to keep private.</p>

        {/* Section 6 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">6) Data Retention</h2>
        <p className="text-white/80 mb-2">We retain information for as long as necessary to:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li>provide the Service</li>
          <li>comply with legal obligations</li>
          <li>resolve disputes</li>
          <li>enforce agreements</li>
        </ul>
        <p className="text-white/70 mb-8">You may request deletion of your account as described below.</p>

        {/* Section 7 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">7) Your Choices and Rights</h2>
        
        <h3 className="text-lg font-semibold text-white mb-2">A. Account information</h3>
        <p className="text-white/70 mb-4">You may update your account information through your account settings (if available).</p>
        
        <h3 className="text-lg font-semibold text-white mb-2">B. Account deletion</h3>
        <p className="text-white/80 mb-2">
          You may request deletion of your account by contacting us at:{' '}
          <a href="mailto:pmk9000@protonmail.com" className="text-amber-500 hover:underline">
            pmk9000@protonmail.com
          </a>
        </p>
        <p className="text-white/70 mb-4">
          When you request deletion, we will delete or anonymize personal information where reasonably possible, subject to legal and operational requirements.
        </p>
        
        <h3 className="text-lg font-semibold text-white mb-2">C. Marketing and analytics choices</h3>
        <p className="text-white/70 mb-2">If we send marketing emails, you can opt out using the unsubscribe link in those emails.</p>
        <p className="text-white/70 mb-8">You can also limit tracking through browser cookie settings and privacy controls.</p>

        {/* Section 8 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">8) Security</h2>
        <p className="text-white/70 mb-8">
          We use reasonable administrative, technical, and physical safeguards designed to protect your information. However, no method of transmission or storage is 100% secure.
        </p>

        {/* Section 9 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">9) Age Restriction (21+)</h2>
        <p className="text-white/70 mb-8">
          The Service is intended only for individuals who are 21 years of age or older. If you are under 21, you may not use the Service and you must not create an account.
        </p>

        {/* Section 10 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">10) International Users</h2>
        <p className="text-white/70 mb-8">
          If you access the Service from outside your country of residence, your information may be processed in countries where we or our service providers operate. Data protection laws may differ from those in your jurisdiction.
        </p>

        {/* Section 11 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">11) Third-Party Links</h2>
        <p className="text-white/70 mb-8">
          The Service may contain links to third-party websites. We are not responsible for the privacy practices of those third parties.
        </p>

        {/* Section 12 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">12) Changes to This Privacy Policy</h2>
        <p className="text-white/70 mb-8">
          We may update this Privacy Policy from time to time. The updated version will be posted on this page with a new effective date.
        </p>

        {/* Section 13 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">13) Contact Us</h2>
        <p className="text-white/80 mb-2">If you have questions about this Privacy Policy, contact:</p>
        <p className="text-white/70 mb-1">Cigar Ranker</p>
        <p className="text-white/70">
          Email:{' '}
          <a href="mailto:pmk9000@protonmail.com" className="text-amber-500 hover:underline">
            pmk9000@protonmail.com
          </a>
        </p>
      </div>

      <BottomNav />
    </div>
  );
};

export default PrivacyPolicyPage;
